import React, { memo, Fragment, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import classNames from 'classnames';

import { RESCUE_POD_BANNER } from 'constants/experience-fragments';

import Spinner from '@johnlewispartnership/wtr-ingredients/ingredients/Spinner';
import ExperienceFragment from 'components/ExperienceFragment';

import styles from 'components/ProductList/ProductList.scss';

import { useWtrSelector } from 'redux/hooks';
import { getScrolledLineNumber } from 'redux/modules/scroll-session-storage/selectors';
import { getPathname } from 'redux/modules/routing/selectors';
import urls from 'constants/urls';

type setAutoFocusIndexCallback = (autoFocusIndex?: number) => void;

interface ProductListProps {
  children?: React.ReactNode;
  contentComponent?: boolean;
  displayInGroups?: boolean;
  id?: string;
  InfiniteScroll?: React.ComponentType<React.PropsWithChildren> | null;
  loading?: boolean;
  LoadMoreButton?: React.ComponentType<{
    setAutoFocusIndex?: setAutoFocusIndexCallback;
  }> | null;
  setAutoFocusIndex?: setAutoFocusIndexCallback;
  withContainer?: boolean;
  withSpinner?: boolean;
}

const ProductList = ({
  children = null,
  contentComponent = false,
  displayInGroups = false,
  id = 'tSr',
  InfiniteScroll = null,
  loading = false,
  LoadMoreButton = null,
  withContainer = true,
  withSpinner = true,
  setAutoFocusIndex,
}: ProductListProps) => {
  const scrolledLineNumber = useWtrSelector(getScrolledLineNumber);
  const pathname = useWtrSelector(getPathname);

  const showRescuePodBanner =
    pathname.includes(urls.groceriesHome) ||
    pathname.includes(urls.search) ||
    pathname.includes(urls.multiSearch);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (scrolledLineNumber) {
      const productPodEl = document.querySelector(`[data-product-id="${scrolledLineNumber}"]`);
      productPodEl?.scrollIntoView();
    }
  }, [loading, scrolledLineNumber]);

  if (!loading && (!children || React.Children.count(children) < 1)) {
    return null;
  }

  const list = (
    <div className={classNames(styles.flexGrid, { [styles.inGroups]: displayInGroups })}>
      {children}
    </div>
  );

  const listWithLoadMore = InfiniteScroll ? (
    <InfiniteScroll>{list}</InfiniteScroll>
  ) : (
    <Fragment>
      {list}
      {LoadMoreButton && <LoadMoreButton setAutoFocusIndex={setAutoFocusIndex} />}
      {showRescuePodBanner && (
        <div data-testid="rescue-pod-banner-container">
          <ExperienceFragment
            experienceFragmentKey={RESCUE_POD_BANNER.key}
            locationName={RESCUE_POD_BANNER.location}
            data-testid="rescue-pod-banner"
          />
        </div>
      )}
    </Fragment>
  );

  return (
    <>
      <Helmet>
        <link
          rel="preconnect"
          href="https://ecom-su-static-prod.wtrecom.com"
          data-testid="link-preconnect"
        />
      </Helmet>
      {withSpinner && (
        <div className={classNames('container-fluid', { [styles.spinnerWrapper]: loading })}>
          <Spinner ariaTextInactive="Loading finished" isActive={loading} />
        </div>
      )}
      {!loading && (
        <div
          className={classNames(styles.productList, {
            [styles.contentComponent]: contentComponent,
          })}
          data-testid="product-list"
          id={id}
        >
          <div
            className={classNames({
              'container-fluid': withContainer,
            })}
          >
            {listWithLoadMore}
          </div>
        </div>
      )}
    </>
  );
};

ProductList.displayName = 'ProductList';

export default memo(ProductList);
